/**
 * PLAT-10935 — Creator Studio's half of `useAITextGeneration`, built once.
 * Lands with the quick-edit surfaces because they are its first two callers;
 * the settings panel (PLAT-10920) is the third and adds nothing to it.
 *
 * Creator Studio now has three places that need a generation — the transcript
 * chip's quick edit (`LeftSidePanel`), the canvas Edit pill
 * (`CompositionCanvasActionsLayer`, via `Stage`) and the composition settings
 * panel (`CompositionControls`) — and all of them answer the surface-specific
 * questions the hook asks identically:
 *
 *  - the transcript is the STORY transcript from `TranscriptionStore`, because
 *    the Creator Studio editor store carries none (PLAT-10935 DS1);
 *  - there is no `clipId`: Creator Studio edits the story video, not a clip, so
 *    the Brand Kit context (E3) is story + showcase (PLAT-10935 DS3);
 *  - the write-back needs an immediate `refreshSource()`, since the command's
 *    own sync is debounced.
 *
 * Three hand-rolled copies of that is precisely how "the same graphic behaves
 * the same wherever it is edited" stops being true — the same reason
 * `buildAITextPopoverContext` exists one level up.
 *
 * Each caller still gets its OWN generation instance, deliberately: the hook's
 * status map is per instance and the callers are separate component trees. The
 * cross-surface race that matters is guarded by the module-level run token
 * inside `useAITextGeneration`, not by sharing state here.
 */
import {
  useEditor,
  useTranscription,
  useVideoSource,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useCallback, useMemo } from 'react';

import { useAITextGeneration } from './useAITextGeneration';

import type { AITextControlSurface } from '@src/analytics/EventTracker';
import type { AITextTranscriptElement } from './useAITextBaseText';
import type { AITextGeneration } from './useAITextGeneration';

export interface CreatorStudioAITextGeneration {
  generation: AITextGeneration;
  /** The story transcript the base text resolves from — the same array the surfaces hand the control. */
  transcriptElements: ReadonlyArray<AITextTranscriptElement> | null | undefined;
}

export function useCreatorStudioAITextGeneration(
  /**
   * PLAT-10923 — which Creator Studio surface is asking. Both callers share
   * every other input, and they are different surfaces in the data: the
   * transcript's quick-edit popover and the composition settings panel.
   */
  surface: AITextControlSurface,
): CreatorStudioAITextGeneration {
  const videoCreator = useVideoCreatorStore();
  const editorStore = useEditor();
  const videoSourceStore = useVideoSource();
  const transcriptionStore = useTranscription();

  const getCurrentVideo = useCallback(
    () => videoCreator.currentVideo,
    [videoCreator],
  );
  const refreshSource = useCallback(
    () => videoSourceStore.refreshSource(),
    [videoSourceStore],
  );
  // BY REFERENCE — the backend resolves the Brand Kit from it (E3).
  const context = useMemo(
    () => ({
      storyId: videoCreator.storyId,
      showcaseId: videoCreator.organization?.id,
    }),
    [videoCreator.storyId, videoCreator.organization?.id],
  );

  const transcriptElements = transcriptionStore.transcriptionElements;

  const generation = useAITextGeneration({
    surface,
    editorStore,
    getCurrentVideo,
    refreshSource,
    context,
    transcriptElements,
  });

  return useMemo(
    () => ({ generation, transcriptElements }),
    [generation, transcriptElements],
  );
}
